import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Download, Share2, User, Calendar, MapPin, Copy } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { useQRCode } from '../hooks/useQRCode';

interface QRCodeDisplayProps {
  voucherCode: string;
  participantName: string;
  hotelName: string;
  startDate: string;
  endDate: string;
  onDownload?: () => void;
  onShare?: () => void;
  className?: string;
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  voucherCode,
  participantName,
  hotelName,
  startDate,
  endDate,
  onDownload,
  onShare,
  className = ''
}) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { downloadQRCode, shareQRCode, copyVoucherCode } = useQRCode();

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Criar dados do QR Code com informações estruturadas
        const qrData = {
          voucher_code: voucherCode,
          participant: participantName,
          hotel: hotelName,
          check_in: startDate,
          check_out: endDate,
          generated_at: new Date().toISOString()
        };

        // Gerar QR Code com os dados JSON
        const qrCodeURL = await QRCode.toDataURL(JSON.stringify(qrData), {
          width: 256,
          margin: 2,
          color: {
            dark: '#1f2937', // gray-800
            light: '#ffffff'
          },
          errorCorrectionLevel: 'M'
        });

        setQrCodeDataURL(qrCodeURL);
      } catch (err) {
        console.error('Error generating QR code:', err);
        setError('Erro ao gerar QR Code');
      } finally {
        setIsLoading(false);
      }
    };

    if (voucherCode) {
      generateQRCode();
    }
  }, [voucherCode, participantName, hotelName, startDate, endDate]);

  const handleDownload = () => {
    if (!qrCodeDataURL) return;
    downloadQRCode(qrCodeDataURL, voucherCode, participantName);
    onDownload?.();
  };

  const handleShare = async () => {
    if (!qrCodeDataURL) return;
    await shareQRCode(qrCodeDataURL, voucherCode, participantName, hotelName);
    onShare?.();
  };

  const handleCopyVoucher = async () => {
    await copyVoucherCode(voucherCode);
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="w-32 h-32 bg-gray-200 rounded-lg mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-xl p-6 text-center ${className}`}>
        <div className="text-red-600 mb-2">⚠️</div>
        <p className="text-red-800 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200 ${className}`}>
      {/* QR Code */}
      <div className="text-center mb-4">
        <div className="inline-block p-4 bg-gray-50 rounded-xl">
          <img 
            src={qrCodeDataURL} 
            alt={`QR Code para ${participantName}`}
            className="w-32 h-32 mx-auto"
          />
        </div>
      </div>

      {/* Participant Info */}
      <div className="text-center mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center justify-center mb-2">
          <User className="h-4 w-4 mr-2 text-blue-600" />
          {participantName}
        </h3>
        <div className="space-y-1 text-sm text-gray-600">
          <div className="flex items-center justify-center">
            <MapPin className="h-3 w-3 mr-1" />
            {hotelName}
          </div>
          <div className="flex items-center justify-center">
            <Calendar className="h-3 w-3 mr-1" />
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
        </div>
      </div>

      {/* Voucher Code */}
      <div className="bg-blue-50 rounded-lg p-3 mb-4">
        <div className="text-center">
          <p className="text-xs text-blue-600 font-medium mb-1">Código do Voucher</p>
          <div className="flex items-center justify-center space-x-2">
            <p className="font-mono text-sm font-bold text-blue-900">{voucherCode}</p>
            <button
              onClick={handleCopyVoucher}
              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
              title="Copiar código"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-2">
        <button
          onClick={handleDownload}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
          title="Download QR Code"
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </button>
        <button
          onClick={handleShare}
          className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors duration-200"
          title="Compartilhar QR Code"
        >
          <Share2 className="h-4 w-4 mr-1" />
          Compartilhar
        </button>
      </div>

      {/* Instructions */}
      <div className="mt-4 p-3 bg-green-50 rounded-lg">
        <p className="text-xs text-green-700 text-center">
          📱 Apresente este QR Code na entrada do hotel
        </p>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
